import { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import MetricCard from "../components/MetricCard";
import { getStatsInvites } from "../api/adminApi";
import type { StatsInvitesResponse } from "../types/admin";

function ViralMetrics() {
  const [data, setData] = useState<StatsInvitesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setError(null);
      setLoading(true);
      const result = await getStatsInvites();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load invite data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        <p>{error}</p>
        <button onClick={loadData} style={{ marginTop: "1rem" }}>
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { funnel, daily } = data;

  // Funnel visualization data
  const funnelData = [
    { name: "Sent", value: funnel.sent, color: "#3b82f6" },
    { name: "Delivered", value: funnel.delivered, color: "#6366f1" },
    { name: "Clicked", value: funnel.clicked, color: "#8b5cf6" },
    { name: "Installed", value: funnel.installed, color: "#a855f7" },
    { name: "Accepted", value: funnel.accepted, color: "#22c55e" },
  ];

  // Calculate K-factor approximation
  const kFactor = funnel.sent > 0 ? (funnel.accepted / funnel.sent) * 2 : 0;

  return (
    <div>
      <header className="page-header">
        <h1 className="page-title">Viral Metrics</h1>
        <p className="page-subtitle">
          {data.period.start} to {data.period.end}
        </p>
      </header>

      {/* Summary Metrics */}
      <div className="metrics-grid">
        <MetricCard
          label="K-Factor"
          value={kFactor.toFixed(2)}
          emphasis={kFactor >= 1 ? "success" : kFactor >= 0.5 ? "warning" : "default"}
        />
        <MetricCard
          label="Overall Conversion"
          value={`${(funnel.overallConversionRate * 100).toFixed(1)}%`}
        />
        <MetricCard label="Invites Sent" value={funnel.sent} />
        <MetricCard label="Users Acquired" value={funnel.accepted} />
      </div>

      {/* Funnel Visualization */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Invite Funnel</h3>
        </div>
        <div className="card-body">
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={funnelData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis type="number" stroke="#94a3b8" tick={{ fill: "#94a3b8", fontSize: 12 }} />
                <YAxis
                  type="category"
                  dataKey="name"
                  stroke="#94a3b8"
                  tick={{ fill: "#94a3b8", fontSize: 12 }}
                  width={80}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1e293b",
                    border: "1px solid #475569",
                    borderRadius: "8px",
                  }}
                  labelStyle={{ color: "#f1f5f9" }}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {funnelData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Step-by-Step Conversion Rates */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Step-by-Step Conversion</h3>
        </div>
        <div className="card-body">
          <div className="metrics-grid">
            <MetricCard
              label="Sent → Delivered"
              value={`${(funnel.deliveredRate * 100).toFixed(1)}%`}
            />
            <MetricCard label="Delivered → Clicked" value={`${(funnel.clickRate * 100).toFixed(1)}%`} />
            <MetricCard
              label="Clicked → Installed"
              value={`${(funnel.installRate * 100).toFixed(1)}%`}
            />
            <MetricCard
              label="Installed → Accepted"
              value={`${(funnel.acceptRate * 100).toFixed(1)}%`}
            />
          </div>
        </div>
      </div>

      {/* Daily Breakdown Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Daily Breakdown</h3>
        </div>
        <div className="card-body">
          <div style={{ overflowX: "auto" }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Sent</th>
                  <th>Delivered</th>
                  <th>Clicked</th>
                  <th>Installed</th>
                  <th>Accepted</th>
                  <th>Declined</th>
                </tr>
              </thead>
              <tbody>
                {daily.slice(0, 14).map((day) => (
                  <tr key={day.date}>
                    <td>{day.date}</td>
                    <td>{day.sent}</td>
                    <td>{day.delivered}</td>
                    <td>{day.clicked}</td>
                    <td>{day.installed}</td>
                    <td style={{ color: "var(--accent-success)" }}>{day.accepted}</td>
                    <td style={{ color: day.declined > 0 ? "var(--accent-danger)" : "inherit" }}>
                      {day.declined}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Viral Growth Explanation */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Understanding K-Factor</h3>
        </div>
        <div className="card-body">
          <p style={{ color: "var(--text-secondary)", marginBottom: "1rem" }}>
            The K-Factor measures viral growth. A K-Factor of:
          </p>
          <ul style={{ color: "var(--text-secondary)", paddingLeft: "1.5rem" }}>
            <li style={{ marginBottom: "0.5rem" }}>
              <strong style={{ color: "var(--accent-success)" }}>&gt; 1.0</strong>: Exponential
              growth (each user brings more than 1 new user)
            </li>
            <li style={{ marginBottom: "0.5rem" }}>
              <strong style={{ color: "var(--accent-warning)" }}>0.5 - 1.0</strong>: Healthy viral
              coefficient (sustainable growth)
            </li>
            <li>
              <strong style={{ color: "var(--text-muted)" }}>&lt; 0.5</strong>: Low virality (need
              to improve invite flow)
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default ViralMetrics;
